import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';

import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';

import InputBookingWaitingList from './InputBookingWaitingList';

import BooksSubmitButton from '../../../../components/books/common/buttons/BooksSubmitButton';
import BooksDateTimePicker from '../../../../components/books/common/inputs/BooksDateTimePicker';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import { radioBehaviorByCheckbox } from './helper';
import { default as Header } from './InputBookingEventDateTimeCommonHeader';

interface Props extends InjectedIntlProps {
  loggedIn: boolean;
  settings: IBooks.SettingEvent;
  postable: boolean;
  onSetDateTimePicker: () => Promise<void>;
  onAddToWaitingList: (timeSlot: IBooks.EventTimeSlot) => Promise<void>;
  onSubmit: (timeSlots: IInputs.EventTimeSlot) => void;
  withExpressFlow: boolean;
}

interface State {
  attendees: number | undefined;
  timeSlots: IBooks.EventTimeSlot[];
  waitingTimeSlot?: IBooks.EventTimeSlot;
}

class InputBookingEventDateTimeList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { selectedTimeSlot, multipleAttendees } = this.props.settings;
    this.state = {
      attendees: multipleAttendees.allowed && !selectedTimeSlot ? undefined : 1,
      timeSlots: selectedTimeSlot ? [selectedTimeSlot] : [],
    };
    this.handleChangeAttendees = this.handleChangeAttendees.bind(this);
    this.handleChangeTimeSlot = this.handleChangeTimeSlot.bind(this);

    this.handleAddToWaitingList = this.handleAddToWaitingList.bind(this);
    this.handleReturnDateTimeList = this.handleReturnDateTimeList.bind(this);

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  public componentDidMount() {
    this.props.onSetDateTimePicker();
  }

  private handleAddToWaitingList(e: React.MouseEvent<HTMLButtonElement>) {
    const { onAddToWaitingList, loggedIn } = this.props;
    const { timeSlots, waitingListProvided } = this.props.settings;
    if (!waitingListProvided || !onAddToWaitingList) {
      return;
    }
    const { value } = e.currentTarget;
    Object.values(timeSlots).forEach(_timeSlots => {
      const timeSlot = _timeSlots.find(t => t.id.toString() === value);
      if (timeSlot) {
        if (!loggedIn) {
          this.setState({ waitingTimeSlot: timeSlot });
          return;
        }
        onAddToWaitingList(timeSlot);
        return;
      }
    });
  }

  private handleReturnDateTimeList() {
    this.setState({ waitingTimeSlot: undefined });
  }

  public handleChangeAttendees(attendees: number) {
    this.setState({ attendees, timeSlots: [] });
  }

  public handleChangeTimeSlot(e: React.ChangeEvent<HTMLInputElement>) {
    const { currentTarget } = e;
    const { settings } = this.props;
    const timeSlot = settings.timeSlotsFlatten.find(
      t => t.id === parseInt(currentTarget.value, 10),
    );
    if (!timeSlot) {
      return;
    }
    this.setState({
      ...this.state,
      timeSlots: radioBehaviorByCheckbox(
        settings.multipleSelectSlots ? 'multiple' : 'single',
        currentTarget.checked,
        this.state.timeSlots,
        timeSlot,
      ),
    });
  }

  private handleSubmit() {
    const { timeSlots, attendees } = this.state;
    this.props.onSubmit({
      timeSlots,
      attendees: attendees || 1,
    });
  }

  public render() {
    const {
      settings,
      postable,
      intl,
      onAddToWaitingList,
      withExpressFlow,
    } = this.props;
    const { attendees, timeSlots, waitingTimeSlot } = this.state;
    return waitingTimeSlot ? (
      <InputBookingWaitingList
        timeSlot={waitingTimeSlot}
        errors={settings.addWaitingListErrors}
        onAddToWaitingList={onAddToWaitingList}
        onReturnSelectDateTime={this.handleReturnDateTimeList}
        postable={postable}
      />
    ) : (
      <div>
        <Header
          settings={settings}
          attendees={attendees}
          onChangeAttendees={this.handleChangeAttendees}
          withExpressFlow={withExpressFlow}
        />
        {attendees && (
          <BooksLabeledContainer
            label={intl.formatMessage({
              id: settings.multipleSelectSlots
                ? 'books.formsLabel.multipleBookingDates'
                : 'books.formsLabel.singleBookingDates',
            })}
          >
            {settings.timeSlotsFlatten.map(timeSlot => (
              <BooksDateTimePicker
                key={timeSlot.id}
                onChange={this.handleChangeTimeSlot}
                postable={postable}
                attendees={attendees}
                waitingListProvided={
                  settings.waitingListProvided !== undefined &&
                  settings.waitingListProvided
                }
                vacancy={timeSlot.vacancy}
                showVacancy={settings.showVacancy}
                value={timeSlot.id}
                checked={!!timeSlots.find(t => t.id === timeSlot.id)}
                date={timeSlot.date}
                startTime={timeSlot.startTime}
                endTime={timeSlot.endTime}
                setNotification={
                  settings.waitingListProvided
                    ? !!settings.waitingList.find(i => i === timeSlot.id)
                    : false
                }
                onClickSetNotification={this.handleAddToWaitingList}
              />
            ))}
          </BooksLabeledContainer>
        )}
        <BooksSubmitButton
          disabled={timeSlots.length === 0 || !postable}
          postable={postable}
          onClick={this.handleSubmit}
        >
          {intl.formatMessage({ id: 'books.submitLabel.next' })}
        </BooksSubmitButton>
      </div>
    );
  }
}

export default injectIntl(InputBookingEventDateTimeList);
